import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Truck, Clock, PackageCheck, AlertTriangle, PackageOpen,
  Pencil, Trash2, Download, Flag,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatCard } from "@/components/kit/StatCard";
import { SearchInput } from "@/components/kit/SearchInput";
import { FilterShell } from "@/components/kit/FilterShell";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { DataTable } from "@/components/tables/DataTable";
import { RowActions } from "@/components/tables/RowActions";
import { EmptyState } from "@/components/feedback/EmptyState";
import { TableSkeleton } from "@/components/feedback/Skeletons";
import { ConfirmDialog } from "@/components/feedback/ConfirmDialog";
import { InboundDrawer } from "./InboundDrawer";
import { ReceiveDrawer } from "./ReceiveDrawer";
import { inboundService, storesService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { formatRelativeTime } from "@/lib/format";
import { useAuth } from "@/hooks/useAuth";
import { canWrite, isOwnerRole } from "@/lib/permissions/permissions";
import { ExportButton } from "@/components/kit/ExportButton";
import { INBOUND_STATUS_META } from "@/lib/constants";

const DEFAULT_FILTERS = { search: "", store: "all", supplier: "", status: "all", date_from: "", date_to: "" };

function progressOf(products = []) {
  const expected = products.reduce((n, p) => n + (p.expected_qty || 0), 0);
  const received = products.reduce((n, p) => n + (p.received_qty || 0), 0);
  const pct = expected > 0 ? Math.min(100, Math.round((received / expected) * 100)) : 0;
  return { expected, received, remaining: Math.max(0, expected - received), pct };
}

function ProgressCell({ products }) {
  const { expected, received, remaining, pct } = progressOf(products);
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="w-40 cursor-default" data-testid="inbound-progress">
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="font-medium tabular-nums text-foreground">{received} / {expected}</span>
              <span className="tabular-nums text-muted-foreground">{pct}%</span>
            </div>
            <Progress value={pct} className="h-1.5" />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-0.5">
            <p>Expected: {expected}</p>
            <p>Received: {received}</p>
            <p>Remaining: {remaining}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function InboundPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const writable = canWrite(user?.role, "inbound");
  const owner = isOwnerRole(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [receiving, setReceiving] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [toFlag, setToFlag] = useState(null);

  useEffect(() => {
    const status = searchParams.get("status");
    if (status) {
      setDraft((d) => ({ ...d, status }));
      setApplied((a) => ({ ...a, status }));
    }
    if (searchParams.get("new") === "1" && writable) {
      setEditing(null);
      setDrawerOpen(true);
    }
    if (status || searchParams.get("new")) setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const params = useMemo(() => ({
    search: applied.search,
    store_id: applied.store === "all" ? "" : applied.store,
    supplier: applied.supplier,
    status: applied.status === "all" ? "" : applied.status,
    date_from: applied.date_from,
    date_to: applied.date_to,
  }), [applied]);

  const listQuery = useQuery({ queryKey: ["inbound", params], queryFn: () => inboundService.list(params) });
  const summaryQuery = useQuery({ queryKey: ["inbound", "summary"], queryFn: inboundService.summary });
  const storesQuery = useQuery({ queryKey: ["stores", "lite"], queryFn: () => storesService.list() });

  const stores = useMemo(() => storesQuery.data?.items || [], [storesQuery.data]);
  const storeMap = useMemo(() => Object.fromEntries(stores.map((s) => [s.id, s.store_name])), [stores]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["inbound"] });
    qc.invalidateQueries({ queryKey: ["inventory"] });
    qc.invalidateQueries({ queryKey: ["orders"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["activity"] });
  };

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (r) => { setEditing(r); setDrawerOpen(true); };

  const cardFilter = (status) => {
    const next = applied.status === status ? "all" : status;
    setDraft((d) => ({ ...d, status: next }));
    setApplied((a) => ({ ...a, status: next }));
  };

  const confirmFlag = async () => {
    try { await inboundService.problem(toFlag.id); toast.success(`${toFlag.inbound_id} flagged as a problem`); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setToFlag(null); }
  };

  const confirmDelete = async () => {
    try { await inboundService.remove(toDelete.id); toast.success("Inbound shipment deleted"); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setToDelete(null); }
  };

  const s = summaryQuery.data || {};
  const rows = listQuery.data?.items || [];

  const rowMenu = (r) => {
    const items = [];
    if (r.status !== "received") {
      items.push({ key: "receive", label: "Receive Shipment", icon: Download, onSelect: () => setReceiving(r) });
    }
    items.push({ key: "edit", label: "Edit", icon: Pencil, onSelect: () => openEdit(r) });
    if (!["problem", "received"].includes(r.status)) {
      items.push({ key: "flag", label: "Flag Problem", icon: Flag, onSelect: () => setToFlag(r) });
    }
    if (owner) {
      items.push({ key: "delete", label: "Delete", icon: Trash2, destructive: true, separatorBefore: true, onSelect: () => setToDelete(r) });
    }
    return items;
  };

  const columns = [
    { key: "inbound_id", header: "Inbound ID", render: (r) => <span className="font-medium text-foreground">{r.inbound_id}</span> },
    { key: "supplier", header: "Supplier", render: (r) => r.supplier || <span className="text-muted-foreground">—</span> },
    { key: "store", header: "Store", render: (r) => storeMap[r.store_id] || <span className="text-muted-foreground">—</span> },
    { key: "sku_count", header: "SKUs", align: "right", render: (r) => <span className="tabular-nums">{r.products?.length || 0}</span> },
    { key: "tracking", header: "Tracking", render: (r) => (r.tracking_numbers?.length ? `${r.tracking_numbers.length} #` : <span className="text-muted-foreground">—</span>) },
    { key: "progress", header: "Progress", render: (r) => <ProgressCell products={r.products} /> },
    {
      key: "status", header: "Status",
      render: (r) => { const m = INBOUND_STATUS_META[r.status] || INBOUND_STATUS_META.expected; return <StatusBadge label={m.label} tone={m.tone} />; },
    },
    { key: "updated", header: "Updated", render: (r) => formatRelativeTime(r.updated_at) },
    { key: "actions", header: "", align: "right", render: (r) => (writable ? <RowActions items={rowMenu(r)} /> : null) },
  ];

  const exportColumns = [
    { label: "Inbound ID", key: "inbound_id" },
    { label: "Supplier", key: "supplier" },
    { label: "Store", get: (r) => storeMap[r.store_id] || "" },
    { label: "SKUs", get: (r) => r.products?.length || 0 },
    { label: "Expected", get: (r) => (r.products || []).reduce((n, p) => n + (p.expected_qty || 0), 0) },
    { label: "Received", get: (r) => (r.products || []).reduce((n, p) => n + (p.received_qty || 0), 0) },
    { label: "Status", get: (r) => INBOUND_STATUS_META[r.status]?.label || r.status },
    { label: "Updated", get: (r) => r.updated_at },
  ];

  return (
    <div className="space-y-6" data-testid="page-inbound">
      <PageHeader
        sticky title="Inbound" description="Track supplier shipments and receive stock into inventory."
        actions={
          <div className="flex items-center gap-2">
            <ExportButton filename="inbound" columns={exportColumns} rows={rows} testId="inbound-export" />
            {writable && (
              <Button className="gap-2" onClick={openCreate} data-testid="inbound-primary-action"><Plus className="h-4 w-4" />New Shipment</Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="stat-expected" label="Expected" value={s.expected ?? "—"} icon={Clock} tone="neutral" onClick={() => cardFilter("expected")} active={applied.status === "expected"} />
        <StatCard testId="stat-partially-received" label="Partially Received" value={s.receiving ?? "—"} icon={Truck} tone="blue" onClick={() => cardFilter("partially_received")} active={applied.status === "partially_received"} />
        <StatCard testId="stat-received" label="Received" value={s.received ?? "—"} icon={PackageCheck} tone="green" onClick={() => cardFilter("received")} active={applied.status === "received"} />
        <StatCard testId="stat-problems" label="Problems" value={s.problems ?? "—"} icon={AlertTriangle} tone="red" onClick={() => cardFilter("problem")} active={applied.status === "problem"} />
      </div>

      <FilterShell
        onApply={() => { setApplied(draft); toast.success("Filters applied"); }}
        onReset={() => { setDraft(DEFAULT_FILTERS); setApplied(DEFAULT_FILTERS); toast("Filters reset"); }}
      >
        <SearchInput value={draft.search} onChange={(v) => setDraft((d) => ({ ...d, search: v }))} placeholder="Inbound ID, tracking, SKU…" />
        <Select value={draft.store} onValueChange={(v) => setDraft((d) => ({ ...d, store: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-store"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All stores</SelectItem>{stores.map((st) => (<SelectItem key={st.id} value={st.id}>{st.store_name}</SelectItem>))}</SelectContent>
        </Select>
        <input value={draft.supplier} onChange={(e) => setDraft((d) => ({ ...d, supplier: e.target.value }))} placeholder="Supplier"
          className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm shadow-soft focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" data-testid="filter-supplier" />
        <Select value={draft.status} onValueChange={(v) => setDraft((d) => ({ ...d, status: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="expected">Expected</SelectItem>
            <SelectItem value="partially_received">Partially Received</SelectItem>
            <SelectItem value="received">Received</SelectItem>
            <SelectItem value="problem">Problem</SelectItem>
          </SelectContent>
        </Select>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">From</label>
          <input type="date" value={draft.date_from} onChange={(e) => setDraft((d) => ({ ...d, date_from: e.target.value }))}
            className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm shadow-soft focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" data-testid="filter-date-from" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">To</label>
          <input type="date" value={draft.date_to} onChange={(e) => setDraft((d) => ({ ...d, date_to: e.target.value }))}
            className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm shadow-soft focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" data-testid="filter-date-to" />
        </div>
      </FilterShell>

      {listQuery.isLoading ? (
        <TableSkeleton rows={6} cols={8} />
      ) : (
        <div className="overflow-x-auto">
          <DataTable
            testId="inbound-table"
            paginated
            columns={columns}
            data={rows}
            emptyState={
              <EmptyState icon={PackageOpen} title="No inbound shipments" testId="inbound-empty-state"
                description="Log a supplier shipment to track its arrival and receive stock into inventory."
                action={writable ? <Button className="gap-2" onClick={openCreate} data-testid="inbound-empty-action"><Plus className="h-4 w-4" /> New Shipment</Button> : null} />
            }
          />
        </div>
      )}

      <InboundDrawer open={drawerOpen} onOpenChange={setDrawerOpen} record={editing} stores={stores} onSaved={refresh} />
      <ReceiveDrawer open={!!receiving} onOpenChange={(o) => !o && setReceiving(null)} record={receiving} storeName={receiving ? storeMap[receiving.store_id] : ""} onSaved={refresh} />
      <ConfirmDialog open={!!toFlag} onOpenChange={(o) => !o && setToFlag(null)}
        title="Flag as problem?" description={`Mark "${toFlag?.inbound_id}" as a problem (damaged, incorrect, or mismatched shipment). This is recorded in the Activity Log.`}
        confirmLabel="Flag problem" destructive onConfirm={confirmFlag} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}
        title="Delete inbound shipment?" description={`This permanently removes "${toDelete?.inbound_id}" and is recorded in the Activity Log.`}
        confirmLabel="Delete shipment" destructive onConfirm={confirmDelete} />
    </div>
  );
}
